// Pure form-state model + serialisers for the record-invoice modal — the
// dependency-light leaf behind RecordInvoiceModal. Kept free of React and the
// api-client so the two-level (charge → split) serialisation, validation, and
// the live-feedback derivations are unit-testable in isolation and so
// extracting it couples no vi.mock() factory to this file.
//
// The modal edits money as text (so a half-typed "12." is a valid interim
// state); these helpers parse those strings into the numeric wire shape the
// backend schema expects and drop empty optionals rather than sending empty
// strings. Existing charge `id`s are preserved for charges that stay ticked
// across the wizard's Back/Next; an unticked-then-reticked payable is
// re-seeded as a new charge (no id) and the backend re-mints one on save.
// Three gates, increasingly strict: Draft (`canSaveDraft`) accepts a
// charge-less invoice — everything else must still be well-formed — for a
// header-only save that the operator finishes later; Save (`canSave`) is the same
// structural validity but requires at least one charge; Approve
// (`canApprove`) additionally requires a balanced, in-budget, on-price
// invoice — advisory, the backend re-checks on approval. `toCreateInput`/
// `toUpdateInput` take an optional `status` ("draft" | "review") that rides
// along on the wire payload only when explicitly asked for.
//
// A draft charge carries a client-only `payableId` picker handle
// (`milestone:<id>` / `rate:<id>` / `sow:<id>` / `line:<id>`) sourced from the
// tag-driven Payables groups. That handle never crosses the wire: at the
// serialisation boundary it is mapped to the stored coverage ref
// (`milestoneId` / `rateItemId` / `sowId`; a `line:` handle carries no ref),
// and on edit the stored coverage ref is reconstructed back into a handle.
// A rated charge's `rate`/`unit` are likewise client-only and never
// persisted, so `stateFromInvoice` takes an optional Payables catalogue to
// re-hydrate them for a stored rate charge on edit — restoring the qty-left
// hints and the qty -> expected-amount recompute that create mode gets for
// free. Omitting the catalogue reproduces today's un-hydrated behaviour.
// There is no per-invoice supplier — the supplier is always the contract's own
// supplier, so no supplier field exists on the form state.
//
// `sharePointUrl` mirrors the backend's isSharePointUrl gate at validation
// time so the form rejects exactly what the API would 400 on. A blank value
// is omitted from the create payload but sent as an explicit `null` on
// update, since update must be able to clear a previously-set link.
//
// Split drafts carry an optional row qty (text, "" = not entered) for
// multi-split rated charges. `splitQtyDefault` derives the seed/display value
// (this row's amount at the charge's invoice-time unit rate — `effectiveUnitRate`,
// preferring the stored expectedAmount ÷ qty pair over the possibly-drifted
// catalogue `rate`) so an untouched box and an absent qty serialise
// identically; `serialiseCharge` only emits `qty` on the wire when the text
// parses to a positive number. `applySplitSync` keeps a live-edited split's
// qty and amount consistent at that same effective rate: a qty edit
// recomputes the amount, and an amount edit unconditionally clears a typed
// qty so it can't strand a stale value that contradicts the edited amount.
// `duplicateSplitLineIds`/`crossChargeDuplicateLineIds` flag a PO line drawn
// more than once (within one charge, or across charges) so the emailed table
// doesn't show ambiguous duplicate rows; `combineSplitsForLine` merges one
// charge's same-line splits into a single row.
//
// The record-invoice modal is a two-step wizard: step 1 is a grouped
// multi-select catalogue of payables (no charges seeded up front — see
// `makeEmptyInvoiceState`), and step 2 confirms/edits the resulting charge
// cards. `selectedPayableIds` reads the current tick set off the draft
// charges; `reconcileSelection` re-derives the charge list from a new tick
// set each time the operator changes step-1 selection, preserving in-place
// edits on drafts that stay ticked and never touching manual (payable-less)
// charges.

import { parseMoney } from "./poFormState";
import { payableIdFromCharge } from "./invoicedByPayable";
import { validateLineBalance, validatePrice } from "../../../utils/financeCalc";
import type {
  Payables,
  Invoice,
  InvoiceChargeInput,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  ChargeSplit,
  LineBalance,
} from "../../../types/finance";

export { parseMoney } from "./poFormState";

// Stable client-only keys for React list identity — never sent to the backend.
let keyCounter = 0;
function nextKey(prefix: string): string {
  keyCounter += 1;
  return `${prefix}-${keyCounter}`;
}

export interface SplitDraft {
  /** Client-only React key; not part of the wire payload. */
  key: string;
  poLineId: string;
  /** Money as text so a half-typed value is a valid interim state. */
  amount: string;
  /** Row quantity as text; "" = not entered (the email derives it). Only
   *  surfaced in the UI for multi-split rated charges. */
  qty: string;
}

export interface ChargeDraft {
  /** Client-only React key; not part of the wire payload. */
  key: string;
  /** Stored charge id, preserved on edit so the charge round-trips. */
  id?: string;
  /** Client-only picker handle for the payable this charge covers
   *  (`milestone:<id>` / `rate:<id>` / `sow:<id>` / `line:<id>`), or undefined
   *  for a manual/ad-hoc charge. Mapped to the stored coverage ref at the wire
   *  boundary — never serialised as-is. */
  payableId?: string;
  label: string;
  /** Agreed price for the axis-2 check; null for a variable/contingency charge
   *  that has no price signal. */
  expectedAmount: number | null;
  /** Editable quantity for a rated product charge (text). */
  qty?: string;
  /** Client-only unit rate captured from the pricing catalogue so a qty edit can
   *  recompute the expected amount; not serialised. Not persisted on the invoice;
   *  re-hydrated from the payables catalogue when a stored rate charge is re-opened
   *  with `payables` supplied. */
  rate?: number | null;
  /** Client-only display unit for a rated charge; not serialised. */
  unit?: string;
  splits: SplitDraft[];
}

export interface InvoiceFormState {
  poId: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: string;
  gstApplies: boolean;
  notes: string;
  sharePointUrl: string;
  charges: ChargeDraft[];
}

export interface InvoiceFieldErrors {
  invoiceNumber?: string;
  invoiceDate?: string;
  totalAmount?: string;
  sharePointUrl?: string;
  /** Form-level "need at least one charge". */
  charges?: string;
}

export interface InvoiceFormErrors {
  fieldErrors: InvoiceFieldErrors;
  /** Per-charge messages, keyed by the draft's client key. */
  chargeErrors: Record<string, string>;
  /** Per-split messages, keyed by the draft's client key. */
  splitErrors: Record<string, string>;
}

// ── builders ────────────────────────────────────────────────────────────────

export function makeEmptySplit(poLineId?: string): SplitDraft {
  return { key: nextKey("split"), poLineId: poLineId ?? "", amount: "", qty: "" };
}

export function makeEmptyCharge(): ChargeDraft {
  return {
    key: nextKey("charge"),
    label: "",
    expectedAmount: null,
    splits: [makeEmptySplit()],
  };
}

export function makeEmptyInvoiceState(poId: string): InvoiceFormState {
  return {
    poId,
    invoiceNumber: "",
    invoiceDate: "",
    totalAmount: "",
    gstApplies: true,
    notes: "",
    sharePointUrl: "",
    // The wizard's step 1 (catalogue tick-list) starts fully unticked; charges
    // are seeded by reconcileSelection as the operator ticks payables.
    charges: [],
  };
}

// ── wizard selection model ────────────────────────────────────────────────────

// Picker handles present on payable-backed drafts — the wizard step 1 tick
// set. Manual charges carry no handle and are never in the selection.
export function selectedPayableIds(state: InvoiceFormState): Set<string> {
  const out = new Set<string>();
  for (const c of state.charges) {
    if (c.payableId) out.add(c.payableId);
  }
  return out;
}

// The catalogue's display order — milestones, then products, SOWs, other —
// which is also the append order for newly ticked charges.
function catalogueOrder(payables: Payables): string[] {
  return [
    ...payables.milestones.map((m) => m.payableId),
    ...payables.products.map((p) => p.payableId),
    ...payables.sows.map((s) => s.payableId),
    ...payables.other.map((o) => o.payableId),
  ];
}

// Reconcile drafts against the step-1 tick set: ticked drafts keep their
// edits, unticked payable drafts are dropped, newly ticked payables append
// freshly seeded drafts in catalogue order, manual drafts always survive.
export function reconcileSelection(
  state: InvoiceFormState,
  selected: ReadonlySet<string>,
  payables: Payables,
  poLineIds: string[],
): InvoiceFormState {
  const kept = state.charges.filter(
    (c) => !c.payableId || selected.has(c.payableId),
  );
  const present = new Set(
    kept.map((c) => c.payableId).filter((id): id is string => !!id),
  );
  const added: ChargeDraft[] = [];
  for (const id of catalogueOrder(payables)) {
    if (!selected.has(id) || present.has(id)) continue;
    const seeded = chargeFromPayable(payables, id, poLineIds);
    if (seeded) added.push(seeded);
  }
  return { ...state, charges: [...kept, ...added] };
}

// ── two-level add / remove / patch ────────────────────────────────────────────

export function addCharge(state: InvoiceFormState): InvoiceFormState {
  return { ...state, charges: [...state.charges, makeEmptyCharge()] };
}

export function removeCharge(state: InvoiceFormState, index: number): InvoiceFormState {
  return { ...state, charges: state.charges.filter((_, i) => i !== index) };
}

export function addSplit(
  state: InvoiceFormState,
  chargeIndex: number,
  poLineId?: string,
): InvoiceFormState {
  return {
    ...state,
    charges: state.charges.map((charge, i) =>
      i === chargeIndex
        ? { ...charge, splits: [...charge.splits, makeEmptySplit(poLineId)] }
        : charge,
    ),
  };
}

export function removeSplit(
  state: InvoiceFormState,
  chargeIndex: number,
  splitIndex: number,
): InvoiceFormState {
  return {
    ...state,
    charges: state.charges.map((charge, i) =>
      i === chargeIndex
        ? { ...charge, splits: charge.splits.filter((_, j) => j !== splitIndex) }
        : charge,
    ),
  };
}

// Apply an edit to a single charge. Editing the quantity of a rated charge (one
// whose unit rate is known) recomputes the expected amount and rewrites the
// first split's amount to match, mirroring the PO line's unitPrice × quantity
// invariant. All other patches are a plain merge.
export function updateCharge(
  charge: ChargeDraft,
  patch: Partial<ChargeDraft>,
): ChargeDraft {
  const next: ChargeDraft = { ...charge, ...patch };
  if ("qty" in patch && next.rate !== undefined && next.rate !== null) {
    const q = parseMoney(next.qty ?? "");
    if (q !== null) {
      const expected = Math.round(next.rate * q * 100) / 100;
      next.expectedAmount = expected;
      next.splits = next.splits.map((s, i) =>
        i === 0 ? { ...s, amount: String(expected) } : s,
      );
    }
  }
  return next;
}

export function patchCharge(
  state: InvoiceFormState,
  index: number,
  patch: Partial<ChargeDraft>,
): InvoiceFormState {
  return {
    ...state,
    charges: state.charges.map((charge, i) =>
      i === index ? updateCharge(charge, patch) : charge,
    ),
  };
}

// The rate frozen into the invoice at recording time. For a rated charge,
// expectedAmount ÷ qty IS that rate — at create time (updateCharge keeps the
// pair in step) and on reopen (both fields are persisted). charge.rate is
// only a fallback: it rehydrates from the CURRENT catalogue, so relying on
// it would silently reprice a historical invoice after rate-card drift.
export function effectiveUnitRate(charge: ChargeDraft): number | null {
  const expected = charge.expectedAmount;
  const qty = charge.qty !== undefined ? parseMoney(charge.qty) : null;
  if (expected !== null && expected !== undefined && expected > 0 && qty !== null && qty > 0) {
    return expected / qty;
  }
  if (charge.rate !== undefined && charge.rate !== null && charge.rate > 0) {
    return charge.rate;
  }
  return null;
}

export interface SplitSyncResult {
  patch: Partial<SplitDraft>;
  adjusted: "amount" | "qty" | null;
}

// Keep a split's qty and amount consistent as either is edited. Qty drives
// money at the invoice-time rate; an amount edit clears a typed qty back to
// the derived display UNCONDITIONALLY — clearing is always safe (display
// falls back to derivation, wire to absence) and must not depend on rate
// availability, or a deleted rate-card entry could strand a stale qty that
// contradicts the edited amount in the stored approval email.
export function applySplitSync(
  charge: ChargeDraft,
  split: SplitDraft,
  patch: Partial<SplitDraft>,
): SplitSyncResult {
  if ("qty" in patch) {
    const rate = effectiveUnitRate(charge);
    const qty = parseMoney(patch.qty ?? "");
    if (rate !== null && qty !== null && qty > 0) {
      return {
        patch: { ...patch, amount: String(Math.round(qty * rate * 100) / 100) },
        adjusted: "amount",
      };
    }
    return { patch, adjusted: null };
  }
  if ("amount" in patch && split.qty !== "") {
    return { patch: { ...patch, qty: "" }, adjusted: "qty" };
  }
  return { patch, adjusted: null };
}

export function patchSplit(
  state: InvoiceFormState,
  chargeIndex: number,
  splitIndex: number,
  patch: Partial<SplitDraft>,
): InvoiceFormState {
  return {
    ...state,
    charges: state.charges.map((charge, i) =>
      i === chargeIndex
        ? {
            ...charge,
            splits: charge.splits.map((split, j) =>
              j === splitIndex ? { ...split, ...patch } : split,
            ),
          }
        : charge,
    ),
  };
}

// ── payable seeding ───────────────────────────────────────────────────────────

// The suggested lines a payable may seed splits on. Scoped to a PO, only
// lines that PO actually has qualify (so re-scoping the PO never seeds a line
// it doesn't have — the split select can't display a foreign line and the
// approve gate would 409 on it); with no PO scope every suggestion stands.
function linesOnPo(suggestedLineIds: string[], poLineIds?: string[]): string[] {
  if (poLineIds && poLineIds.length > 0) {
    return suggestedLineIds.filter((id) => poLineIds.includes(id));
  }
  return suggestedLineIds;
}

// A per-unit payable seeds a single split on its first qualifying line;
// undefined leaves the line unset for the user to pick.
function preferredLine(
  suggestedLineIds: string[],
  poLineIds?: string[],
): string | undefined {
  return linesOnPo(suggestedLineIds, poLineIds)[0];
}

// A lump-sum payable (milestone, SOW) seeds one split per qualifying line:
// when a variation splits a milestone's payment across two budget lines, the
// PO tags it on both and the invoice should draw down both. The form cannot
// know how the payment divides, so the first split carries the whole amount
// and the rest start blank for the user to move money across (or remove).
// Blank is deliberate: it fails validation until the user decides, which
// blocks draft/review saves on that charge but never lets an untouched $0
// row slip into the approval email. No qualifying line → a single unset
// split, as for per-unit payables.
function seedSplitsPerLine(
  suggestedLineIds: string[],
  poLineIds: string[] | undefined,
  amount: number | null,
): SplitDraft[] {
  const lines = linesOnPo(suggestedLineIds, poLineIds);
  if (lines.length === 0) return [seedSplit(undefined, amount)];
  return lines.map((line, i) => seedSplit(line, i === 0 ? amount : null));
}

// Seed a charge from a picked payable: a milestone seeds its payment as the
// expected amount and pre-fills a split per tagged line of the selected PO
// (whole amount on the first); a rated product seeds qty 1 with rate × 1 as
// expected and a single split on its first tagged line; a rate-less product,
// a SOW (balance-only, one blank split per tagged line) or an "other" line
// has no price so expectedAmount is null and split amounts are left blank.
// Returns null for an unknown id.
export function chargeFromPayable(
  payables: Payables,
  payableId: string,
  poLineIds?: string[],
): ChargeDraft | null {
  const milestone = payables.milestones.find((m) => m.payableId === payableId);
  if (milestone) {
    return {
      key: nextKey("charge"),
      payableId,
      label: milestone.label,
      expectedAmount: milestone.expected,
      splits: seedSplitsPerLine(
        milestone.suggestedLineIds,
        poLineIds,
        milestone.expected,
      ),
    };
  }
  const product = payables.products.find((p) => p.payableId === payableId);
  if (product) {
    const rate = product.rate;
    const expected = rate !== null ? rate : null;
    const line = preferredLine(product.suggestedLineIds, poLineIds);
    return {
      key: nextKey("charge"),
      payableId,
      label: product.label,
      expectedAmount: expected,
      qty: rate !== null ? "1" : "",
      rate,
      unit: product.unit,
      splits: [seedSplit(line, expected)],
    };
  }
  const sow = payables.sows.find((s) => s.payableId === payableId);
  if (sow) {
    return {
      key: nextKey("charge"),
      payableId,
      label: sow.label,
      expectedAmount: null,
      splits: seedSplitsPerLine(sow.suggestedLineIds, poLineIds, null),
    };
  }
  const other = payables.other.find((o) => o.payableId === payableId);
  if (other) {
    return {
      key: nextKey("charge"),
      payableId,
      label: other.label,
      expectedAmount: null,
      splits: [seedSplit(other.suggestedLineId, null)],
    };
  }
  return null;
}

function seedSplit(poLineId: string | undefined, amount: number | null): SplitDraft {
  return {
    key: nextKey("split"),
    poLineId: poLineId ?? "",
    amount: amount !== null ? String(amount) : "",
    qty: "",
  };
}

// ── PO reselection ────────────────────────────────────────────────────────────

// Reconstruct the client-only picker handle from a stored charge's coverage
// ref. "line:"/manual charges store no ref, so they reconstruct as manual
// (undefined) — the picker shows them as a manual charge on edit.
// payableIdFromCharge moved to ./invoicedByPayable so the catalogue's
// invoiced-totals fold shares the same charge → payable-handle mapping.

// Point the form at a different PO and re-scope its splits: any split whose
// line no longer exists in the newly-selected PO has its line cleared (the
// entered amount and the charge structure are preserved) so the user re-picks a
// valid line. Chosen over dropping whole splits/charges because it keeps the
// operator's typed amounts and payable picks intact across a mis-click on the
// PO selector.
export function changePo(
  state: InvoiceFormState,
  poId: string,
  poLineIds: string[],
): InvoiceFormState {
  const valid = new Set(poLineIds);
  return {
    ...state,
    poId,
    charges: state.charges.map((charge) => ({
      ...charge,
      splits: charge.splits.map((split) =>
        split.poLineId && !valid.has(split.poLineId)
          ? { ...split, poLineId: "" }
          : split,
      ),
    })),
  };
}

// ── edit round-trip ───────────────────────────────────────────────────────────

export function stateFromInvoice(inv: Invoice, payables?: Payables): InvoiceFormState {
  return {
    poId: inv.poId,
    invoiceNumber: inv.invoiceNumber,
    invoiceDate: inv.invoiceDate,
    totalAmount: String(inv.totalAmount),
    gstApplies: inv.gstApplies ?? true,
    notes: inv.notes ?? "",
    sharePointUrl: inv.sharePointUrl ?? "",
    charges: inv.charges.map((c) => {
      const payableId = payableIdFromCharge(c);
      // Rate and unit are client-only draft fields (never persisted), so a
      // re-opened rate charge re-hydrates them from the payables catalogue —
      // restoring the qty-left hints AND create-mode's qty -> expected-amount
      // recompute in edit mode. Non-rate charges stay un-hydrated, and when no
      // catalogue is supplied this is a no-op (today's behaviour).
      const product =
        payables && payableId?.startsWith("rate:")
          ? payables.products.find((p) => p.payableId === payableId)
          : undefined;
      return {
        key: nextKey("charge"),
        id: c.id,
        payableId,
        label: c.label,
        expectedAmount: c.expectedAmount ?? null,
        qty: c.qty !== undefined ? String(c.qty) : undefined,
        rate: product ? product.rate : undefined,
        unit: product?.unit,
        splits: c.splits.map((s) => ({
          key: nextKey("split"),
          poLineId: s.poLineId,
          amount: String(s.amount),
          qty: s.qty !== undefined ? String(s.qty) : "",
        })),
      };
    }),
  };
}

// ── validation ────────────────────────────────────────────────────────────────

// Mirror of the backend's isSharePointUrl gate (models/finance.ts): https
// only, sharepoint.com or a *.sharepoint.com subdomain, no embedded
// credentials. Kept in lockstep so the form rejects exactly what the API
// would 400 on.
function isSharePointUrl(raw: string): boolean {
  if (raw.length > 2048) return false;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  if (url.username !== "" || url.password !== "") return false;
  const host = url.hostname.toLowerCase();
  return host === "sharepoint.com" || host.endsWith(".sharepoint.com");
}

export function validateInvoiceForm(
  state: InvoiceFormState,
  opts?: { requireCharges?: boolean },
): InvoiceFormErrors {
  const requireCharges = opts?.requireCharges ?? true;
  const fieldErrors: InvoiceFieldErrors = {};
  if (!state.invoiceNumber.trim()) {
    fieldErrors.invoiceNumber = "Invoice number is required";
  }
  if (!state.invoiceDate.trim()) {
    fieldErrors.invoiceDate = "Invoice date is required";
  } else if (Number.isNaN(Date.parse(state.invoiceDate))) {
    fieldErrors.invoiceDate = "Enter a valid date";
  }
  const total = parseMoney(state.totalAmount);
  if (total === null || total < 0) {
    fieldErrors.totalAmount = "Total must be a non-negative amount";
  }
  if (requireCharges && state.charges.length === 0) {
    fieldErrors.charges = "Add at least one charge";
  }
  const sp = state.sharePointUrl.trim();
  if (sp !== "" && !isSharePointUrl(sp)) {
    fieldErrors.sharePointUrl = "Enter an https:// SharePoint link (…sharepoint.com)";
  }

  const chargeErrors: Record<string, string> = {};
  const splitErrors: Record<string, string> = {};
  for (const charge of state.charges) {
    if (charge.splits.length === 0) {
      chargeErrors[charge.key] = "Add at least one PO-line split";
      continue;
    }
    for (const split of charge.splits) {
      const amount = parseMoney(split.amount);
      if (!split.poLineId) {
        splitErrors[split.key] = "Choose a PO line";
      } else if (amount === null || amount < 0) {
        splitErrors[split.key] = "Amount must be a non-negative number";
      }
    }
  }
  return { fieldErrors, chargeErrors, splitErrors };
}

export function hasInvoiceErrors(errors: InvoiceFormErrors): boolean {
  return (
    Object.keys(errors.fieldErrors).length > 0 ||
    Object.keys(errors.chargeErrors).length > 0 ||
    Object.keys(errors.splitErrors).length > 0
  );
}

// ── serialisation ─────────────────────────────────────────────────────────────

// Map the client-only picker handle to the stored coverage ref. A `line:`
// handle (an "other"/contingency PO line) and a manual charge carry no ref.
function coverageRef(
  payableId: string | undefined,
): Pick<InvoiceChargeInput, "milestoneId" | "rateItemId" | "sowId"> {
  if (!payableId) return {};
  const sep = payableId.indexOf(":");
  if (sep < 0) return {};
  const prefix = payableId.slice(0, sep);
  const id = payableId.slice(sep + 1);
  if (prefix === "milestone") return { milestoneId: id };
  if (prefix === "rate") return { rateItemId: id };
  if (prefix === "sow") return { sowId: id };
  return {};
}

function serialiseCharge(charge: ChargeDraft): InvoiceChargeInput {
  // Mirrors the UI's showQty gate at the wire boundary — the split qty input
  // only renders for a multi-split charge, so a single-split charge's row qty
  // is the charge Quantity by definition and must never be emitted here. A
  // charge that drops from multi- to single-split can otherwise leave a stale
  // hidden qty on its one remaining split that silently overrides the visible
  // charge-level Quantity in the stored approval email.
  const multiSplit = charge.splits.length > 1;
  const splits: ChargeSplit[] = charge.splits.map((s) => {
    const out: ChargeSplit = { poLineId: s.poLineId, amount: parseMoney(s.amount) ?? 0 };
    if (multiSplit) {
      const qty = parseMoney(s.qty);
      if (qty !== null && qty > 0) out.qty = qty;
    }
    return out;
  });
  const out: InvoiceChargeInput = {
    label: charge.label.trim(),
    splits,
    ...coverageRef(charge.payableId),
  };
  if (charge.id) out.id = charge.id;
  // Omit a null expected amount rather than wire-serialising null — the backend
  // derivation treats an absent and a null expected amount identically (no price
  // signal), so sending it would be a dead field.
  if (charge.expectedAmount !== null) out.expectedAmount = charge.expectedAmount;
  const qty = charge.qty !== undefined ? parseMoney(charge.qty) : null;
  if (qty !== null) out.qty = qty;
  return out;
}

export function toCreateInput(
  state: InvoiceFormState,
  opts?: { status?: "draft" | "review" },
): CreateInvoiceInput {
  const input: CreateInvoiceInput = {
    poId: state.poId,
    invoiceNumber: state.invoiceNumber.trim(),
    invoiceDate: state.invoiceDate.trim(),
    totalAmount: parseMoney(state.totalAmount) ?? 0,
    gstApplies: state.gstApplies,
    charges: state.charges.map(serialiseCharge),
  };
  if (state.notes.trim()) input.notes = state.notes.trim();
  if (state.sharePointUrl.trim()) input.sharePointUrl = state.sharePointUrl.trim();
  if (opts?.status) input.status = opts.status;
  return input;
}

export function toUpdateInput(
  state: InvoiceFormState,
  version: number,
  opts?: { status?: "draft" | "review" },
): UpdateInvoiceInput {
  return {
    ...toCreateInput(state, opts),
    version,
    // Blank on update is an explicit clear (REMOVE server-side); create-mode
    // blank simply omits the field.
    sharePointUrl: state.sharePointUrl.trim() || null,
  };
}

// ── live-feedback derivations ─────────────────────────────────────────────────

// Seed/display value for a multi-split rated charge's qty box: this row's
// amount at the charge's unit rate. The email derives the same number for a
// blank box, so an untouched seed and an absent qty are equivalent.
export function splitQtyDefault(charge: ChargeDraft, split: SplitDraft): string {
  const rate = effectiveUnitRate(charge);
  if (rate === null) return "";
  const amount = parseMoney(split.amount);
  if (amount === null) return "";
  return String(Math.round((amount / rate) * 100) / 100);
}

export function chargeSum(charge: ChargeDraft): number {
  return charge.splits.reduce((acc, s) => acc + (parseMoney(s.amount) ?? 0), 0);
}

export function allocatedTotal(state: InvoiceFormState): number {
  return state.charges.reduce((acc, c) => acc + chargeSum(c), 0);
}

export function isBalanced(state: InvoiceFormState): boolean {
  const total = parseMoney(state.totalAmount) ?? 0;
  return Math.abs(allocatedTotal(state) - total) <= 0.005;
}

// A split is overdrawn when drawing its amount against the line's live
// pre-this-invoice remaining pushes the balance below zero.
export function hasOverdrawnSplit(
  state: InvoiceFormState,
  lineBalances: Record<string, LineBalance>,
): boolean {
  for (const charge of state.charges) {
    for (const split of charge.splits) {
      const amount = parseMoney(split.amount);
      if (amount === null) continue;
      const lb = lineBalances[split.poLineId];
      if (!lb) continue;
      const result = validateLineBalance(
        { remaining: lb.remaining, lineTotal: lb.lineTotal },
        amount,
      );
      if (result.kind === "over") return true;
    }
  }
  return false;
}

export function hasOverPriceCharge(state: InvoiceFormState): boolean {
  for (const charge of state.charges) {
    if (charge.expectedAmount === null) continue;
    if (validatePrice(chargeSum(charge), charge.expectedAmount).kind === "over") {
      return true;
    }
  }
  return false;
}

// Save only needs the invoice to be structurally well-formed.
export function canSave(state: InvoiceFormState): boolean {
  return !hasInvoiceErrors(validateInvoiceForm(state));
}

// Draft gate: structural validity minus the "at least one charge" rule — the
// whole point of a draft is that the charge breakdown can wait, but any
// charge that IS present must still be well-formed.
export function canSaveDraft(state: InvoiceFormState): boolean {
  return !hasInvoiceErrors(validateInvoiceForm(state, { requireCharges: false }));
}

// Approve is the full advisory gate: structurally valid AND balanced AND no
// overdrawn split AND no over-price charge.
export function canApprove(
  state: InvoiceFormState,
  lineBalances: Record<string, LineBalance>,
): boolean {
  return (
    canSave(state) &&
    isBalanced(state) &&
    !hasOverdrawnSplit(state, lineBalances) &&
    !hasOverPriceCharge(state)
  );
}

// ── duplicate detection + combine ─────────────────────────────────────────────

// PO line ids chosen by more than one of this charge's splits (blank line
// selections ignored) — the emailed table would show identical-looking rows.
export function duplicateSplitLineIds(charge: ChargeDraft): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const s of charge.splits) {
    if (!s.poLineId) continue;
    if (seen.has(s.poLineId)) dupes.add(s.poLineId);
    seen.add(s.poLineId);
  }
  return [...dupes];
}

// PO line ids drawn by splits of MORE THAN ONE charge. Advisory only —
// merging across charges would sum quantities with different units, so the
// invoice-level notice informs rather than offering Combine.
export function crossChargeDuplicateLineIds(state: InvoiceFormState): string[] {
  const byLine = new Map<string, Set<number>>();
  state.charges.forEach((charge, i) => {
    for (const s of charge.splits) {
      if (!s.poLineId) continue;
      (byLine.get(s.poLineId) ?? byLine.set(s.poLineId, new Set()).get(s.poLineId)!).add(i);
    }
  });
  return [...byLine.entries()].filter(([, cs]) => cs.size > 1).map(([id]) => id);
}

// Merge one charge's same-line splits into a single row at the first
// occurrence's position: amounts summed (2 dp), qty cleared so the display
// re-derives from the merged amount. Other rows and charges untouched.
export function combineSplitsForLine(
  state: InvoiceFormState,
  chargeIndex: number,
  poLineId: string,
): InvoiceFormState {
  return {
    ...state,
    charges: state.charges.map((charge, i) => {
      if (i !== chargeIndex) return charge;
      const targets = charge.splits.filter((s) => s.poLineId === poLineId);
      if (targets.length < 2) return charge;
      const sum = targets.reduce((acc, s) => acc + (parseMoney(s.amount) ?? 0), 0);
      const merged: SplitDraft = {
        ...targets[0],
        amount: String(Math.round(sum * 100) / 100),
        qty: "",
      };
      let placed = false;
      const splits = charge.splits.flatMap((s) => {
        if (s.poLineId !== poLineId) return [s];
        if (placed) return [];
        placed = true;
        return [merged];
      });
      return { ...charge, splits };
    }),
  };
}

// ── contention ────────────────────────────────────────────────────────────────

export interface LineContention {
  pendingElsewhere: number;
  contendingInvoiceNumbers: string[];
}

// Amounts other in-review invoices on the same PO have pending against each
// line, plus the (deduplicated, in-order) invoice numbers contending for it.
// The invoice being edited is excluded so a review invoice never contends with
// itself. Mirrors the pending-elsewhere logic in financeCalc.deriveInvoice but
// keyed by line for the whole PO at once.
export function contentionByLine(
  invoices: Invoice[],
  poId: string,
  excludeInvoiceId?: string,
): Record<string, LineContention> {
  const out: Record<string, LineContention> = {};
  for (const inv of invoices) {
    if (inv.poId !== poId || inv.status !== "review") continue;
    if (excludeInvoiceId && inv.id === excludeInvoiceId) continue;
    for (const charge of inv.charges) {
      for (const split of charge.splits) {
        const entry =
          out[split.poLineId] ??
          (out[split.poLineId] = {
            pendingElsewhere: 0,
            contendingInvoiceNumbers: [],
          });
        entry.pendingElsewhere += split.amount;
        if (!entry.contendingInvoiceNumbers.includes(inv.invoiceNumber)) {
          entry.contendingInvoiceNumbers.push(inv.invoiceNumber);
        }
      }
    }
  }
  return out;
}
